import { useQuery } from "@tanstack/react-query";
import {
  getCoachPlayerDevelopment,
  getMyPlayerDevelopment,
} from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type { DevelopmentProfile } from "@/lib/types";

/**
 * Player Development profile hooks (M7) — one shape, two surfaces.
 *
 * `useCoachPlayerDevelopment` — the coach modal opened from
 *   Roster. Scope-keyed + gated on `teamId` like the other coach
 *   queries. The coach payload carries the coach-only `themes` +
 *   `linked_accounts`.
 *
 * `useMyPlayerDevelopment` — the viewer Development sub-tab. NOT
 *   scope-keyed (same reasoning as the `/feedback` bundle — the
 *   viewer has no scope switcher). The verb collapses a 404
 *   (unknown OR unrelated player) to `null`, so the caller treats
 *   `null` as "profile not available" without distinguishing the
 *   two — that's the anti-probe contract.
 *
 * Both are gated on `playerId` truthiness so they don't fire with
 * an empty id mid-render (the viewer tab resolves its active
 * player before mounting the profile).
 */

export function useCoachPlayerDevelopment(playerId: string | null) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery<DevelopmentProfile>({
    queryKey: useScopedKey("coach-player-development", {
      playerId: playerId ?? "",
    }),
    queryFn: () => getCoachPlayerDevelopment(playerId as string),
    enabled: !!teamId && !!playerId,
  });
}

export function useMyPlayerDevelopment(playerId: string | null) {
  return useQuery<DevelopmentProfile | null>({
    // Not scope-keyed — viewer surface, no scope switcher.
    queryKey: ["my-player-development", playerId ?? ""],
    queryFn: () => getMyPlayerDevelopment(playerId as string),
    enabled: !!playerId,
  });
}
